import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, SearchBar } from '../ui';
import { MinistriesMegaDropdown } from '../ui';
import LanguageSelector from './LanguageSelector';
import { cn } from '../../utils/class-names-util';
import { ROUTES } from '../../constants/routes';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMinistriesDropdownOpen, setIsMinistriesDropdownOpen] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMinistriesMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsMinistriesDropdownOpen(true);
  };

  const handleMinistriesMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsMinistriesDropdownOpen(false);
    }, 500); // 500ms delay before closing (increased for better UX)
  };

  const handleMegaMenuClose = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsMinistriesDropdownOpen(false);
  };
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Expert scroll detection for professional header behavior
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);  // Updated navigation order: Home, Ministries, Schools, Events, E-Books, About
  // Note: Blogs code kept but removed from navigation as requested
  const navItems = [
    { path: ROUTES.HOME, label: t('nav.home', 'Home') },
    { path: ROUTES.EVENTS, label: t('nav.events', 'Events') },
    { path: ROUTES.SCHOOLS, label: t('nav.schools', 'Schools') },
    // { path: ROUTES.BLOGS, label: t('nav.blogs', 'Blogs') }, // Kept code but removed from navigation
    { path: ROUTES.STORE, label: t('nav.store', 'Store') },
    { path: ROUTES.ABOUT, label: t('nav.about', 'About') },
  ];

  return (
    <>
      <header 
        className={cn(
          "sticky top-0 z-50 w-full transition-all duration-300 shadow-lg",
          isScrolled 
            ? "bg-white/95 backdrop-blur-md border-b border-gray-200/50" 
            : "bg-black/95 backdrop-blur-md border-b border-gray-800/50"
        )}
      >
        <div className="container-church">
          <nav className="flex items-center justify-between h-16 lg:h-20">
          {/* Enhanced Logo */}
          <Link
            to={ROUTES.HOME}
            className="flex items-center space-x-3 group"
          >
            {/* <div className="relative w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-br from-white to-gray-100 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
              <span className="text-black text-lg lg:text-xl font-bold">Z</span>
              <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
            </div> */}
            <div className="flex flex-col">
              <span 
                className={cn(
                  "text-xl lg:text-2xl font-bold tracking-tight transition-colors duration-300",
                  isScrolled 
                    ? "text-black group-hover:text-gray-700" 
                    : "text-white group-hover:text-gray-200"
                )}
              >
                ZOE
              </span>
              <span 
                className={cn(
                  "text-xs lg:text-sm font-medium -mt-1 transition-colors duration-300",
                  isScrolled 
                    ? "text-gray-600 group-hover:text-gray-800" 
                    : "text-gray-400 group-hover:text-gray-300"
                )}
              >
                {/* For All Nations */}
              </span>
            </div>
          </Link>

          {/* Enhanced Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              to={ROUTES.HOME}
              className={cn(
                'relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg',
                isScrolled 
                  ? 'text-black hover:text-gray-700 hover:bg-black/5' 
                  : 'text-white hover:text-gray-200 hover:bg-white/5',
                location.pathname === ROUTES.HOME && (isScrolled ? 'text-gray-700 bg-black/10' : 'text-gray-200 bg-white/10')
              )}
            >
              {t('nav.home')}
              {location.pathname === ROUTES.HOME && (
                <div 
                  className={cn(
                    "absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 rounded-full transition-colors duration-300",
                    isScrolled ? "bg-black" : "bg-white"
                  )}
                ></div>
              )}
            </Link>
            
            {/* Enhanced Ministries with Mega Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleMinistriesMouseEnter}
              onMouseLeave={handleMinistriesMouseLeave}
            >
              <Link
                to={ROUTES.MINISTRIES}
                className={cn(
                  'relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg flex items-center',
                  isScrolled 
                    ? 'text-black hover:text-gray-700 hover:bg-black/5' 
                    : 'text-white hover:text-gray-200 hover:bg-white/5',
                  location.pathname.startsWith(ROUTES.MINISTRIES) && (isScrolled ? 'text-gray-700 bg-black/10' : 'text-gray-200 bg-white/10')
                )}
              >
                {t('nav.ministries')}
                <svg 
                  className={cn(
                    'w-4 h-4 ml-1 transition-transform duration-200',
                    isMinistriesDropdownOpen && 'rotate-180'
                  )} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {location.pathname.startsWith(ROUTES.MINISTRIES) && (
                  <div 
                    className={cn(
                      "absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 rounded-full transition-colors duration-300",
                      isScrolled ? "bg-black" : "bg-white"
                    )}
                  ></div>
                )}
              </Link>
              
              {/* Mega Dropdown positioned relative to this nav item */}
              <MinistriesMegaDropdown 
                isOpen={isMinistriesDropdownOpen}
                onClose={handleMegaMenuClose}
              />
            </div>

            {navItems.slice(1).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg',
                  isScrolled 
                    ? 'text-black hover:text-gray-700 hover:bg-black/5' 
                    : 'text-white hover:text-gray-200 hover:bg-white/5',
                  location.pathname === item.path && (isScrolled ? 'text-gray-700 bg-black/10' : 'text-gray-200 bg-white/10')
                )}
              >
                {item.label}
                {location.pathname === item.path && (
                  <div 
                    className={cn(
                      "absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 rounded-full transition-colors duration-300",
                      isScrolled ? "bg-black" : "bg-white"
                    )}
                  ></div>
                )}
              </Link>
            ))}
          </div>

          {/* Enhanced Controls */}
          <div className="flex items-center space-x-3">
            {/* Expandable Search Bar */}
            <SearchBar isScrolled={isScrolled} expandable={true} className="hidden md:block" />

            {/* Enhanced Mobile Menu Button */}
            <Button 
              size="sm" 
              variant="ghost" 
              className={cn(
                "lg:hidden p-2 rounded-lg transition-all duration-300",
                isScrolled 
                  ? "text-black hover:text-gray-700 hover:bg-black/10" 
                  : "text-white hover:text-gray-300 hover:bg-white/10"
              )}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <svg 
                className={cn(
                  'w-6 h-6 transition-transform duration-200',
                  isMobileMenuOpen && 'rotate-90'
                )} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                />
              </svg>
            </Button>

            {/* Professional Language Selector */}
            <LanguageSelector 
              isScrolled={isScrolled} 
              variant="header" 
              className="hidden sm:block"
            />
          </div>
        </nav>

        {/* Enhanced Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className={cn(
              "lg:hidden absolute top-full left-0 right-0 backdrop-blur-sm shadow-xl transition-all duration-300",
              isScrolled 
                ? "bg-white/95 border-b border-gray-200/50" 
                : "bg-black/95 border-b border-gray-800/50"
            )}
          >
            <div className="container-church py-4 space-y-2">
              <Link
                to="/"
                className={cn(
                  'block px-4 py-3 text-sm font-medium transition-all duration-200 rounded-lg',
                  isScrolled
                    ? 'text-black hover:text-gray-700 hover:bg-black/5'
                    : 'text-white hover:text-gray-200 hover:bg-white/5',
                  location.pathname === ROUTES.HOME && (isScrolled ? 'text-gray-700 bg-black/10' : 'text-gray-200 bg-white/10')
                )}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.home')}
              </Link>
              
              <Link
                to={ROUTES.MINISTRIES}
                className={cn(
                  'block px-4 py-3 text-sm font-medium transition-all duration-200 rounded-lg',
                  isScrolled
                    ? 'text-black hover:text-gray-700 hover:bg-black/5'
                    : 'text-white hover:text-gray-200 hover:bg-white/5',
                  location.pathname.startsWith(ROUTES.MINISTRIES) && (isScrolled ? 'text-gray-700 bg-black/10' : 'text-gray-200 bg-white/10')
                )}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.ministries')}
              </Link>

              {navItems.slice(1).map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'block px-4 py-3 text-sm font-medium transition-all duration-200 rounded-lg',
                    isScrolled
                      ? 'text-black hover:text-gray-700 hover:bg-black/5'
                      : 'text-white hover:text-gray-200 hover:bg-white/5',
                    location.pathname === item.path && (isScrolled ? 'text-gray-700 bg-black/10' : 'text-gray-200 bg-white/10')
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Language Selector for Mobile */}
              <div className="px-4 pt-4 border-t border-gray-200/20">
                <LanguageSelector 
                  isScrolled={isScrolled} 
                  variant="header" 
                  className="w-full"
                />
              </div>
              
              {/* Search bar hidden on mobile screens */}
            </div>
          </div>
        )}
      </div>
    </header>

  </>
  );
};

export default Header;